import { Palette } from '@mui/material/styles';

// Helper functions for button styling
export const pxToRem = (px: number) => `${px / 16}rem`;

export const rgba = (color: string, opacity: number) => 
  `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;

export const boxShadow = (offset: number[], blur: number[], color: string, opacity: number) => 
  `${offset[0]}px ${offset[1]}px ${blur[0]}px ${blur[1]}px ${rgba(color, opacity)}`;

export const linearGradient = (color1: string, color2: string) => 
  `linear-gradient(195deg, ${color1}, ${color2})`;

// Theme-aware color definitions
export const getButtonThemeColors = (palette: Palette) => ({
  white: { main: '#ffffff', focus: '#ffffff' },
  text: { main: palette.text.secondary },
  transparent: { main: 'transparent' },
  grey: { 600: palette.grey[600] },
});

// Theme-aware gradient definitions
export const getButtonGradients = (palette: Palette) => ({
  primary: { 
    main: palette.primary.main, 
    state: palette.primary.dark 
  },
  secondary: { 
    main: palette.secondary.main, 
    state: palette.secondary.dark 
  },
  info: { 
    main: palette.info.main, 
    state: palette.info.dark 
  },
  success: { 
    main: palette.success.main, 
    state: palette.success.dark 
  },
  warning: { 
    main: palette.warning.main, 
    state: palette.warning.dark 
  },
  error: { 
    main: palette.error.main, 
    state: palette.error.dark 
  },
  light: { main: '#f8f9fa', state: '#dee2e6' },
  dark: { main: '#495057', state: '#212529' },
});

// Button color configuration
export const getButtonColorConfig = () => ({
  primary: true,
  secondary: true,
  info: true,
  success: true,
  warning: true,
  error: true,
  light: false,
  dark: true,
});

// Helper function to get color values from palette (for buttons)
export const getColorValue = (palette: Palette, colorName: string) => {
  const colors = getButtonThemeColors(palette);
  
  switch (colorName) {
    case 'primary':
      return { main: palette.primary.main, focus: palette.primary.dark };
    case 'secondary':
      return { main: palette.secondary.main, focus: palette.secondary.dark };
    case 'info':
      return { main: palette.info.main, focus: palette.info.dark };
    case 'success':
      return { main: palette.success.main, focus: palette.success.dark };
    case 'warning':
      return { main: palette.warning.main, focus: palette.warning.dark };
    case 'error':
      return { main: palette.error.main, focus: palette.error.dark };
    case 'white':
      return colors.white;
    case 'light':
      return { main: '#f8f9fa', focus: '#e9ecef' };
    case 'dark':
      return { main: '#495057', focus: '#343a40' };
    default:
      return colors.white;
  }
};

// Helper function to get simple color values from palette (for progress, etc.)
export const getSimpleColorValue = (palette: Palette, colorName: string) => {
  switch (colorName) {
    case 'primary':
      return palette.primary.main;
    case 'secondary':
      return palette.secondary.main;
    case 'info':
      return palette.info.main;
    case 'success':
      return palette.success.main;
    case 'warning':
      return palette.warning.main;
    case 'error':
      return palette.error.main;
    case 'light':
      return '#f8f9fa';
    case 'dark':
      return '#495057';
    default:
      return palette.info.main;
  }
};